import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GlobalNav from '../components/GlobalNav';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../AuthContext';
import { getOrder, createPaymentOrder, verifyPayment } from '../api';
import { CreditCard, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const CheckoutPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user && orderId) {
      getOrder(orderId).then(res => setOrder(res.data)).catch(() => navigate('/dashboard'));
    }
  }, [user, orderId, navigate]);

  const handlePayment = async () => {
    setProcessing(true);
    try {
      // Create mock payment order
      const paymentOrder = await createPaymentOrder({
        order_id: orderId,
        amount: order.amount
      });

      // Simulate payment success after 2 seconds
      setTimeout(async () => {
        try {
          await verifyPayment(orderId, paymentOrder.data.id);
          setPaymentComplete(true);
          toast.success('Payment successful!');
        } catch (error) {
          toast.error('Payment verification failed');
          setProcessing(false);
        }
      }, 2000);
    } catch (error) {
      toast.error('Payment failed');
      setProcessing(false);
    }
  };

  if (loading || !order) {
    return <div className="min-h-screen bg-background flex items-center justify-center">Loading...</div>;
  }

  if (paymentComplete) {
    return (
      <div className="min-h-screen bg-background" data-testid="checkout-success">
        <div className="container mx-auto px-4 md:px-8 py-6">
          <GlobalNav />
        </div>
        <div className="container mx-auto px-4 md:px-8 py-12 flex items-center justify-center min-h-[60vh]">
          <Card className="w-full max-w-md text-center">
            <CardContent className="py-12">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" data-testid="payment-success-icon" />
              <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
              <p className="text-muted-foreground mb-6">
                Your order has been placed successfully. We'll start processing it shortly.
              </p>
              <Button onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-button">
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="checkout-page">
      <div className="container mx-auto px-4 md:px-8 py-6">
        <GlobalNav />
      </div>

      <div className="container mx-auto px-4 md:px-8 py-12">
        <h1 className="text-4xl font-bold tracking-tight mb-8" data-testid="checkout-title">Checkout</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="font-medium" data-testid="order-service-name">{order.service_name}</span>
                    <span className="text-muted-foreground">Order #{order.id.slice(0, 8)}</span>
                  </div>
                  <div className="border-t pt-4 flex justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span className="text-accent" data-testid="checkout-total">₹{order.amount.toLocaleString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payment Method (Mock)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  This is a mock payment for demonstration. Click the button below to simulate payment.
                </p>
                <div className="bg-secondary/50 p-4 rounded-lg">
                  <p className="text-sm font-medium mb-2">Mock Payment Details:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Amount: ₹{order.amount.toLocaleString()}</li>
                    <li>• Payment will be processed instantly</li>
                    <li>• No actual charges will be made</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Payment</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-secondary/50 p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">Amount to Pay</p>
                    <p className="text-3xl font-bold text-accent">₹{order.amount.toLocaleString()}</p>
                  </div>
                  <Button 
                    className="w-full" 
                    size="lg" 
                    onClick={handlePayment}
                    disabled={processing}
                    data-testid="pay-now-button"
                  >
                    {processing ? 'Processing...' : 'Pay Now (Mock)'}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    Secure mock payment processing
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutPage;